import is from "is_js"; // quiet
import { Log } from "../Log/Log.js"; // quiet
import { Messages, MessagePayload } from "../Messages/Messages.js"; // quiet

import { Environment } from "./Environment.js";
import { PermissionStatuses } from "./PermissionStatuses.js"
import { GyroMessages } from "./GyroMessages.js"
import { GyroErrors } from "./GyroErrors.js";
import { States } from "./States.js";


//==============================================================================
/**
 * @object child
 * @description  handles all functionality from the iframed child side of things
 */
const child = ( function() {

  //----------------------------------------------------------------------------
  // init these vars, yo.
  let _provider       = "",
      _state          = States.READY;        


  //----------------------------------------------------------------------------
  const __e = ( msg ) => { throw new Error( msg ); }


  //----------------------------------------------------------------------------
  /**
   * initializer function
   * @param  {string} provider  only handle message events from this provider
   * @return {@link Gyro.child}
   */
  const init = ( provider ) => new Promise( ( resolve, reject ) => 
    {
      Log.info( "Gyro.child", "init(), Called." );

      // state check
      if( _state !== States.READY )
        throw new Error( "library has already been initialized." );

      _state = States.INITING;
      _provider = provider;

      const __inited = ( s ) =>
        {
          Log.debug( "Gyro.child", "__inited" );

          // only work for non-desktop devices (mobile/tablets)    
          _state = s 
                 ? s 
                 : is.desktop()
                 ? States.INACTIVE  
                 : States.IDLE;      
          resolve();
        };

      // nothing to do if this page is iframed or the device is not running iOS13+
      if( Environment.isIFramed )
      {
        Log.debug( "Gyro.child", "  iframed, handshaking..." );
        handshake()
          .then( () => {
              Log.debug( "Gyro.child", "handshake().then..." );
              __inited();
            })
          .catch( (e) => {
              Log.debug( "Gyro.child", "handshake().catch..." );
              __inited( States.INACTIVE );
            });
      }
      else 
      {
        Log.debug( "Gyro.child", "  not iframed..." );
        // if this isn't an IOS with permissions, we're done.
        if( !Environment.isIOS13 )
        {
          Log.debug( "Gyro.child", "  doesn't need permissions..." );
          __inited();
        }

        Log.debug( "Gyro.child", "  needs permissions..." );

        // check initial permissions
        _state = States.REQUESTING;
        _requestChild()
          .then( () => 
            {
              Log.debug( "Gyro.child", "_requestChild().then..." );
              __inited();
            });
      }
    });


  //----------------------------------------------------------------------------
  /**
   * makes a call to `DeviceMotionEvent.requestPermission` within this window
   * @return {Promise}
   */
  const _requestChild  = () => new Promise( ( resolve, reject ) =>
    {
      Log.info( "Gyro.child", "_requestChild()" );
      Log.debug( "Gyro.child", "  prev _state:", _state );
      const prevState = _state;
      _state = States.REQUESTING;
      Log.debug( "Gyro.child", "  now _state:", _state );
      Log.debug( "Gyro.child", "  permissionStatus:", Environment.permissionStatus );

      Log.debug( "Gyro.child", "  requesting..." );
      DeviceMotionEvent.requestPermission()
        .then( access => 
          {
            Log.debug( "GyroFrame.child", "  requestPermission().then..." );
            Environment.permissionStatus = access;
          })
        .catch( e => 
          { 
            Log.debug( "GyroFrame.child", "  requestPermission().catch... e:", e );
          })
        .finally( () => 
          {
            Log.debug( "GyroFrame.child", "  requestPermission().finally..." );
            _state = prevState;
            resolve();
          });
    });

  //----------------------------------------------------------------------------
  /**
   * communicates with the parent component to request permissions
   * @return {Promise}
   */
  const _requestParent = () => new Promise( ( resolve, reject ) => 
    { 
      Log.info( "Gyro.child", "_requestParent()" );
      Log.debug( "Gyro.child", "  permissionStatus:", Environment.permissionStatus );

      // if the status is already known... 
      if( Environment.permissionStatus !== PermissionStatuses.UNKNOWN ) { resolve(); }

      // set a 10s timer to get a message from the parent
      let timer = setTimeout( function() {
        window.removeEventListener( "message", __onMessage_processed__ );
        reject();
      }, 10 * 1000 );

      // 'processed' message handler 
      const __onMessage_processed__ = ( e ) =>
        {
          // first, kill the timer
          clearTimeout( timer );

          let payload = e.data;
          Log.debug( "Gyro.child", "__onMessage_processed__(), payload:", payload );

          // provider check
          if( !payload || 
              payload.provider !== _provider ||
              payload.message !== GyroMessages.PROCESSED )
            return;

          // snag the status
          Environment.permissionStatus = payload.data.status;
          Log.debug( "Gyro.child", "  permissionStatus:", Environment.permissionStatus );

          // don't want this to keep firing.
          window.removeEventListener( "message", __onMessage_processed__ );

          // glad that's all done
          resolve();       
        };

      // set up the 'processed' handler
      Log.debug( "Gyro.child", "  adding message handler..." );
      window.addEventListener( "message", __onMessage_processed__ );

      // ET phone home! (and by home, i mean the parent page)
      _pm( GyroMessages.REQUEST );
      Log.debug( "Gyro.child", "_requestParent(), Done." );
    });

  //----------------------------------------------------------------------------
  /**
   * Communicates with the parent page to turn on DeviceMotion events getting
   * sent over. 
   * 
   * @return {Promise} 
   */
  const enable = () => new Promise( ( resolve, reject ) => 
    {
      Log.info( "Gyro.child", "enable(), _state:", _state );

      // eeny meeny miny mo
      switch( _state )
      {
        // all states during which an 'enable' request cannot be made.
        case States.INACTIVE   : reject({ error: GyroErrors.NO_GYRO });      break;
        case States.INITING    : reject({ error: GyroErrors.INITIALIZING }); break; 
        case States.REQUESTING : reject({ error: GyroErrors.REQUESTING });   break; 
        case States.ENABLING   : reject({ error: GyroErrors.ENABLING });     break;
        
        // already enabled
        case States.ENABLED    : resolve(); break;

        // we've gotten all the ways in which we can't or shouldn't enable out 
        // of the way. let's try to enable this bitch, shall we?
        case States.IDLE :

          Log.debug( "Gyro.child", "  can be enabled..." );

          if( !Environment.isIFramed )
          {
            Log.debug( "Gyro.child", "  not iframed..." );

            // if we have permissions, request em.
            if( Environment.hasPermissions )
            {
              Log.debug( "Gyro.child", "  has permissions...." );
              Log.debug( "Gyro.child", "  permissionStatus:", Environment.permissionStatus );

              _requestChild()
                .then( () => 
                  {
                    Log.info( "Gyro.child", "_requestChild().then..." );
                    Log.debug( "Gyro.child", "  permssionStatus:", Environment.permissionStatus );
                    _state = ( Environment.permissionStatus === PermissionStatuses.GRANTED ) 
                           ? States.ENABLED
                           : States.INACTIVE;
                    Log.debug( "Gyro.child", "  _state:", _state );
                    resolve();
                  });
            }
            // otherwise, we're done.
            else
            {
              Log.debug( "Gyro.child", "  no permissions, nothing to do..." );
              _state = States.ENABLED;
              resolve();
            }

            // **************
            // is there anymore more to do here?
            // try to capture deviceorientation events before THREEjs gets to them?
            // rebroadcast based on this _isEnabled var?
            // **************
          }

          // we're iframed, so check in with the parent
          else
          { 
            Log.debug( "Gyro.child", "  iframed..." );

            // set up the return handler
            const __onMessage_enabled__ = (e) =>
              {
                // clearTimeout( timer );

                let payload = e.data;
                Log.debug( "Gyro.child", "__onMessage_enabled__(), payload:", payload );

                // make sure this message is for us
                if( !payload || 
                    payload.provider !== _provider ||
                    !payload.message || 
                    ( payload.message !== GyroMessages.ERROR && 
                      payload.message !== GyroMessages.ENABLED ))
                  return;

                // parent gave us an error
                if( payload.message === GyroMessages.ERROR )
                {
                  Log.debug( "Gyro.child", "  ERROR:", payload.data.error );
                  _state = States.INACTIVE;
                }

                if( payload.message === GyroMessages.ENABLED )
                {
                  Log.debug( "Gyro.child", "  adding 'motion' listener..." );
                  // get ready for those `motion` messages
                  window.addEventListener( "message", _onMessage_motion );
                  _state = States.ENABLED;
                }

                Log.debug( "Gyro.child", "  removing 'enabled' listener..." );

                // don't need this anymore
                window.removeEventListener( "message", __onMessage_enabled__ );

                Log.debug( "Gyro.child", "  _state:", _state );

                // fin!
                resolve();       
              };
            
            Log.debug( "Gyro.child", "  adding 'enabled' listener..." );
            window.addEventListener( "message", __onMessage_enabled__ );
            _pm( GyroMessages.ENABLE );
          }

          break;

        default:
          // not a gotdamn thing.
      }
    });

  //----------------------------------------------------------------------------
  /**
   * Communicates with the parent page to turn off DeviceMotion events from
   * getting sent. we could just simply stop doing anything with those messages,
   * but this is bad. 1) the parent is still handling those vents, i.e. cpu
   * cycles; 2) sending useless messages clogs up the intertubes. let's keep
   * the internet tubes clean. 
   * 
   * @return {Promise} 
   */
  const disable = () => new Promise( ( resolve, reject ) => 
    {
      Log.info( "Gyro.child", "disable(), _state:", _state );

      // eeny meeny miny mo
      switch( _state )
      {
        // all states during which an 'disable' request cannot be made.
        case States.INACTIVE   : reject({ error: GyroErrors.NO_GYRO });      break;
        case States.INITING    : reject({ error: GyroErrors.INITIALIZING }); break; 
        case States.REQUESTING : reject({ error: GyroErrors.REQUESTING });   break; 
        case States.ENABLING   : reject({ error: GyroErrors.ENABLING });     break;
        case States.DISABLING  : reject({ error: GyroErrors.ENABLING });     break;
        
        // already disabled
        case States.IDLE       : resolve(); break;

        // we've gotten all the ways in which we can't or shouldn't disable out 
        // of the way. let's try to disable this bitch, shall we?
        case States.ENABLED :

          Log.debug( "Gyro.child", "  can be disabled..." );

          if( !Environment.isIFramed )
          {
            Log.debug( "Gyro.child", "  not iframed..." );

            _state = States.IDLE;
            Log.debug( "Gyro.child", "  _state:", _state );

            // **************
            // is there anymore more to do here? try the converse of what we're 
            // doing in the enable function -- killing the rebroadcast of 
            // 'deviceorientation' events?
            // **************

            resolve();
          }

          // we're iframed, so check in with the parent
          else
          { 
            Log.debug( "Gyro.child", "  iframed..." );

            // set up the return handler
            const __onMessage_disabled__ = (e) =>
              {
                // clearTimeout( timer );

                let payload = e.data;
                Log.debug( "Gyro.child", "__onMessage_processed(), payload:", payload );

                // make sure this message is for us
                if( !payload || 
                    payload.provider !== _provider ||
                    !payload.message || 
                    ( payload.message !== GyroMessages.ERROR && 
                      payload.message !== GyroMessages.DISABLED ))
                  return;

                Log.debug( "Gyro.child", "  the message is for us..." );

                // parent gave us an error
                if( payload.message === GyroMessages.ERROR )
                {
                  Log( "  ERROR:", payload.data.error );

                  // ************
                  // handle error code accordingly?
                  // ************
                }

                // don't need this anymore
                Log.debug( "Gyro.child", "  removing 'disabled' listener..." );
                window.removeEventListener( "message", __onMessage_disabled__ );

                if( payload.message === GyroMessages.DISABLED )
                {
                  // get ready for those `motion` messages
                  Log.debug( "Gyro.child", "  removing 'motion' listener..." );
                  window.removeEventListener( "message", _onMessage_motion );
                }

                // set the state
                _state = States.IDLE;
                Log.debug( "Gyro.child", "  _state:", _state );

                // ************
                first = true;
                // ************

                // fin!
                resolve();       
              };
            

            Log.debug( "Gyro.child", "  adding 'disabled' listener..." );
            window.addEventListener( "message", __onMessage_disabled__ );
            _pm( GyroMessages.DISABLE );
          }

          break;

        default:
          // not a gotdamn thing.
      }
    });

  //----------------------------------------------------------------------------
  /**
   * shortcut function for a postMessage call
   * @param {Object} payload  data obj to send
   * @param {Object} target   target window (default is parent)
   */
  const _pm = ( message, target = window.parent ) =>
    {
      let payload = new MessagePayload( _provider, message );

      Log.info( "Gyro.child", "_pm(), payload:", payload );
      target.postMessage( payload, "*" );
    };

  //----------------------------------------------------------------------------
  /**
   * handler for `motion` messages
   * @param  {Event}  e    MessageEvent object
   */
  let first = true;
  const _onMessage_motion = ( e ) =>
    {
      let payload = e.data;

      // not the right provider or message? GTFO.
      if( !payload ||
          payload.provider !== _provider ||
          payload.message !== GyroMessages.MOTION ) return;


      let event;
      try {
        // this is working in iOS devices, but was throwing errors in Android, 
        // specifically, the call to `initDeviceOrientationEvent`. Come to find 
        // out, this method is deprecated, but iOS is still using it. Android is
        // using the new method, in the catch block. I could have just done a
        // check for iOS, but this is good enough. Might want to run some timing
        // tests in the future to see if this or a device check is quicker.
        event = document.createEvent( "DeviceOrientationEvent" );
        event.initDeviceOrientationEvent(
          "deviceorientation", 
          true, 
          true, 
          payload.data.event.alpha, 
          payload.data.event.beta, 
          payload.data.event.gamma, 
          true
        );
        first && Log.debug( "Gyro.child", "_onMessage_motion(), (create) event:", event );
      } catch( e ) {
        event = new DeviceOrientationEvent( "deviceorientation", {
          bubbles    : true,
          cancelable : true,
          alpha      : payload.data.event.alpha, 
          beta       : payload.data.event.beta, 
          gamma      : payload.data.event.gamma
        })
        first && Log.debug( "Gyro.child", "_onMessage_motion(), (constructor) event:", event );
      }

      first = false;
      window.dispatchEvent( event );
    };

  //----------------------------------------------------------------------------
  /**
   * checks if Gyro.parent is present in the container page
   * @return {Promise} 
   */
  const handshake = () => new Promise( ( resolve, reject ) =>
    {
      let timer = setTimeout( function() 
        {
          Log.debug( "  no handshake received" );
          window.removeEventListener( "message", __onMessage_handshake__ );
          reject();
        }, 3000 );

      const __onMessage_handshake__ = (e) =>
        {
          let payload = e.data;
          Log.debug( "Gyro.child", "__onMessage_handshake__(), payload:", payload );

          // make sure this message is for us
          if( !payload || 
              payload.provider !== _provider ||
              !payload.message || 
              payload.message !== GyroMessages.HANDSHOOK )
            return;

          Log.debug( "  handshook..." );
          // don't need these anymore
          window.removeEventListener( "message", __onMessage_handshake__ );
          clearTimeout( timer );

          // fin!
          resolve();       
        };

      window.addEventListener( "message", __onMessage_handshake__ );
      _pm( GyroMessages.HANDSHAKE );
    });


  //============================================================================
  /**
   * expose those private parts to the world! 
   */
  return { 

    // getters
    "provider"     : () => _provider,
    "state"        : () => _state,

    // // public methods
    "init"         : init,
    "enable"       : enable,
    "disable"      : disable
  }
})();

export { child as default };
